<template>
  <div
    :class="[bemBlock, 'product-detail', 'product-detail-vue']"
    :data-context="context"
  >
    <BaseProductDataForSfra :product="product" />
    <button
      :class="
        bemElement('button', {
          selected: selected,
          disabled: disabled,
        })
      "
      :data-url="size.url"
      @click="!selected && selectSize()"
    >
      <span class="visually-hidden">{{ product.productName }}</span>
      <img
        v-if="!!swatchImage"
        :alt="swatchImage.alt"
        :class="[
          bemElement('swatch-img'),
          {
            loading: loadingImage,
          },
        ]"
        :src="swatchImage.url"
        @error="handleImageError"
        @load="loadingImage = false"
      />
      <span
        v-if="disabled"
        :class="
          bemElement('disabled-icon', {
            light: isDarkColor,
          })
        "
      />
    </button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EventBusConfig from '../../config/eventBusConfig';
import bemClassesMixin from '../../mixins/bemClassesMixin';
import productPropSizesMixin from '../../mixins/productPropSizesMixin';
import sfraProductMixin from '../../mixins/sfraProductMixin';
import BaseProductDataForSfra from '../BaseProductDataForSfra.vue';
import { getters, types } from '../../store';
import {
  getVariationAttributeFromProduct,
  isSameVariationGroup,
  isSizeVariation,
} from '../../../common/helpers/productModelHelpers';
import getFrontConfig from '../../../common/getFrontConfig';

const DARK_COLORS = ['T1000'];
const FRONT_CONFIG = getFrontConfig();

export default {
  name: 'TheProductCustomizerMain',

  components: {
    BaseProductDataForSfra,
  },

  mixins: [bemClassesMixin, productPropSizesMixin, sfraProductMixin],

  props: {
    product: {
      type: Object,
      required: true,
      default() {
        return {};
      },
    },
    initiallySelected: {
      type: Boolean,
      required: true,
      default: false,
    },
    reset: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      disabled: false,
      loadingImage: true,
      currentSize: null,
      context: FRONT_CONFIG.vue.TheProductCustomizer.context.productCustomizer,
    };
  },

  computed: {
    ...mapGetters({
      selectedMainProduct: getters.productCustomizer.selectedMainProduct,
    }),
    color() {
      const colorAttr = getVariationAttributeFromProduct(this.product, 'color');
      if (colorAttr && colorAttr.values.length) {
        return colorAttr.values.find((value) => value.selected);
      }
      return null;
    },
    isDarkColor() {
      return DARK_COLORS.includes(this.color?.id);
    },
    selectable() {
      return this.size.selectable;
    },
    selected() {
      return this.selectedMainProduct?.pid
        ? isSameVariationGroup(this.selectedMainProduct?.pid, this.product?.id)
        : false;
    },
    size() {
      return this.getUniqueSize();
    },
    swatchImage() {
      const swatch = this.color?.images?.swatch?.[0];
      return swatch && swatch.url ? swatch : null;
    },
  },

  watch: {
    reset() {
      if (this.reset) {
        this.initComplementState();
      }
    },
    selected() {
      if (!this.selected) {
        this.deselectSize();
      }
    },
    currentSize() {
      this.sfraSelectSize(this.currentSize);
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.initComplementState();
      this.disabled = !this.selectable;
    });
    this.$eventBus.$on(
      EventBusConfig.commands.TheProductDetails.updateProduct,
      this.handleSelectVariation
    );
  },

  beforeDestroy() {
    this.$eventBus.$off(
      EventBusConfig.commands.TheProductDetails.updateProduct,
      this.handleSelectVariation
    );
  },

  methods: {
    handleImageError(e) {
      const noImagePlaceholderColor = '#e0e0e0';
      const noImagePlaceholder = `<svg xmlns='http://www.w3.org/2000/svg' width='8' height='8'><rect width='8' height='8' fill='${noImagePlaceholderColor}'/></svg>`;
      const noImagePlaceholderUrl = `data:image/svg+xml,${encodeURIComponent(
        noImagePlaceholder
      )}`;
      const noImage = {
        alt: '',
        title: '',
        url: noImagePlaceholderUrl,
      };
      const img = e.target;
      img.src = noImage.url;
    },
    handleSelectVariation({ product, container }) {
      if (container.dataset.context !== this.context) {
        return;
      }
      const isCurrent = isSameVariationGroup(product.id, this.product.id);
      if (isCurrent) {
        if (product.available && isSizeVariation(this.product, product)) {
          const mainProduct = {
            pid: product.id,
            qty: product.selectedQuantity,
            price: product.price.sales
              ? product.price.sales.value
              : product.price.list.value,
            storeId: null,
          };
          this.$store.dispatch(
            types.productCustomizer.selectMainProduct,
            mainProduct
          );
        }
        this.disabled = !product.available;
      }
    },
    deselectSize() {
      this.currentSize = { url: this.sizeResetUrl };
    },
    selectSize() {
      this.currentSize = this.size;
    },
    initComplementState() {
      if (this.initiallySelected && this.selectable) {
        this.selectSize();
      }
    },
  },
};
</script>
